import React, { useEffect, useState } from 'react';
import { db } from '../../firebase';
import { collection, query, where, onSnapshot, doc, getDocs, deleteDoc, updateDoc, increment } from 'firebase/firestore';
import './MyPaiement.css';
import { 
  FaCreditCard, 
  FaMoneyBillWave, 
  FaTicketAlt, 
  FaCheck, 
  FaTimes, 
  FaSpinner,
  FaExclamationTriangle,
  FaCheckCircle,
  FaInfoCircle,
  FaPrint,
  FaTrash,
  FaSearch,
  FaUser,
  FaTable,
  FaEnvelope,
  FaShoppingCart,
  FaEuroSign,
  FaFileExcel,
  FaArrowUp,
  FaArrowDown,
  FaList,
  FaTh
} from 'react-icons/fa';

const MyPaiement = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [currentClientId, setCurrentClientId] = useState(null);
  const [selectedPayments, setSelectedPayments] = useState({
    espece: false,
    cb_comptoir: false,
    ticket_resto: false
  });
  const [setPaymentAmounts] = useState({
    espece: '',
    cb_comptoir: '',
    ticket_resto: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy] = useState('status');
  const [sortOrder] = useState('desc');
  const [viewMode, setViewMode] = useState('cards');
  const [showStats, setShowStats] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  useEffect(() => {
    // Récupérer les clients avec paiement (tous les statuts)
    const fetchClients = async () => {
      try {
        const clientsCollection = collection(db, 'clients');
        const q = query(clientsCollection, where('paiement', '!=', null));  // Récupérer tous les clients ayant un objet paiement

        onSnapshot(q, async (querySnapshot) => {
          const clientsData = await Promise.all(
            querySnapshot.docs.map(async (docSnapshot) => {
              const clientData = docSnapshot.data();
              const clientId = docSnapshot.id;
        
              // Récupérer les détails de la commande pour ce client
              const commandesCollection = collection(db, `clients/${clientId}/commande`);
              const commandesSnapshot = await getDocs(commandesCollection);
              let totalCommande = 0;
              let items = [];
        
              commandesSnapshot.forEach((doc) => {
                const commandeData = doc.data();
                items = [...items, ...commandeData.items];
                totalCommande += commandeData.items.reduce((acc, item) => acc + item.prix * item.quantity, 0);
              });

              const tableLabel = clientData.table === 'a_emporter' ? 'À Emporter' : `Numéro de Table ${clientData.table}`;
        
              return {
                id: clientId,
                prenom: clientData.prenom,
                table: tableLabel,
                email: clientData.email,
                idclient: clientData.idclient,
                paiement: clientData.paiement,
                items,
                totalCommande,
              };
            })
          );
          setClients(clientsData);
          setLoading(false);
        });        
      } catch (error) {
        console.error('Erreur lors de la récupération des clients:', error);
        setLoading(false);
      }
    };

    fetchClients();
  }, []);

  // Fonction pour imprimer le ticket
  const handleImprimerTicket = (client) => {
    const ticketContent = `
      Résumé de commande :
      --------------------
      Client: ${client.prenom}
      Numéro de Table: ${client.table}
      Numéro de Commande: ${client.idclient}
      Email: ${client.email}
      
      Détails de la commande :
      --------------------
      ${client.items && client.items.length > 0 ? client.items.map((item, index) => `
        ${item.nom} - Quantité: ${item.quantity} - Prix Unitaire: ${item.prix.toFixed(2)} €
        Prix Total: ${(item.prix * item.quantity).toFixed(2)} €
      `).join('\n') : 'Aucun article dans la commande.'}
      
      Total de la commande: ${client.totalCommande.toFixed(2)} €
      
      Merci de votre commande !`;
    
    const printWindow = window.open('', '', 'height=600,width=800');
    printWindow.document.write('<html><head><title>Ticket de caisse</title></head><body>');
    printWindow.document.write(`<pre>${ticketContent}</pre>`);
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    printWindow.print();
  };

  if (loading) {
    return (
      <div className="mypaiement-loading-container">
        <div className="mypaiement-loading-spinner">
          <FaSpinner className="spinning" />
        </div>
        <p className="mypaiement-loading-text">Chargement des paiements...</p>
      </div>
    );
  }

  // Fonction pour afficher un message
  const showMessage = (text, type) => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => {
      setMessage('');
      setMessageType('');
    }, 5000);
  };

  // Fonction pour obtenir l'icône du statut
  const getStatusIcon = (status) => {
    switch (status) {
      case 'payé':
        return <FaCheckCircle className="mypaiement-status-icon mypaiement-status-paid" />;
      case 'en_attente':
        return <FaExclamationTriangle className="mypaiement-status-icon mypaiement-status-pending" />;
      default:
        return <FaInfoCircle className="mypaiement-status-icon mypaiement-status-default" />;
    }
  };

  // Fonction pour obtenir l'icône du moyen de paiement
  const getPaymentMethodIcon = (method) => {
    switch (method) {
      case 'espece':
        return <FaMoneyBillWave className="mypaiement-payment-icon" />;
      case 'cb_comptoir':
        return <FaCreditCard className="mypaiement-payment-icon" />;
      case 'ticket_resto':
        return <FaTicketAlt className="mypaiement-payment-icon" />;
      default:
        return <FaEuroSign className="mypaiement-payment-icon" />;
    }
  };

  // Calculer les statistiques
  const calculateStats = () => {
    const totalClients = clients.length;
    const paidClients = clients.filter(client => client.paiement.status === 'payé').length;
    const pendingClients = clients.filter(client => client.paiement.status === 'en_attente').length;
    const totalRevenue = clients
      .filter(client => client.paiement.status === 'payé')
      .reduce((sum, client) => sum + client.totalCommande, 0);
    
    return {
      totalClients,
      paidClients,
      pendingClients,
      totalRevenue
    };
  };

  const stats = calculateStats();

  // Filtrage et tri des clients
  const filteredClients = clients.filter(client => {
    const matchesSearch = 
      client.prenom.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.idclient.toString().includes(searchTerm) ||
      client.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || client.paiement.status === filterStatus;
    
    return matchesSearch && matchesStatus;
  }).sort((a, b) => {
    // Toujours prioriser les paiements en attente
    if (a.paiement.status === 'en_attente' && b.paiement.status !== 'en_attente') {
      return -1;
    }
    if (a.paiement.status !== 'en_attente' && b.paiement.status === 'en_attente') {
      return 1;
    }
    
    // Si même statut, trier selon le critère sélectionné
    let comparison = 0;
    if (sortBy === 'date') {
      comparison = b.idclient - a.idclient;
    } else if (sortBy === 'name') {
      comparison = a.prenom.localeCompare(b.prenom);
    } else if (sortBy === 'amount') {
      comparison = a.totalCommande - b.totalCommande;
    } else if (sortBy === 'status') {
      comparison = a.paiement.status.localeCompare(b.paiement.status);
    }
    return sortOrder === 'asc' ? comparison : -comparison;
  });

  // Export Excel
  const exportToExcel = async () => {
    setExporting(true);
    try {
      const XLSX = await import('xlsx');
      
      const excelData = clients.map(client => ({
        'ID Client': client.idclient,
        'Prénom': client.prenom,
        'Email': client.email,
        'Table': client.table,
        'Statut': client.paiement.status,
        'Total': client.totalCommande,
        'Moyens de Paiement': client.paiement.moyen_paiement || 'Non défini',
        'Nombre d\'articles': client.items ? client.items.length : 0
      }));

      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      XLSX.utils.book_append_sheet(workbook, worksheet, "Paiements");
      
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `paiements-${new Date().toISOString().split('T')[0]}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      showMessage('Export réussi', 'success');
    } catch (error) {
      console.error('Erreur lors de l\'export:', error);
      showMessage('Erreur lors de l\'export', 'error');
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteCommande = async (clientId) => {
    try {
      const clientRef = doc(db, 'clients', clientId);
      await deleteDoc(clientRef);
      showMessage('Commande supprimée avec succès', 'success');
    } catch (error) {
      console.error('Erreur lors de la suppression de la commande:', error);
      showMessage('Erreur lors de la suppression', 'error');
    }
  };  

  const updatePaymentCounters = async () => {
    try {
      const paiementRef = doc(db, 'paiement', 'paiement');
      
      // Créer un objet pour les mises à jour
      const updates = {};
      
      if (selectedPayments.espece) {
        updates.espece = increment(1);
      }
      if (selectedPayments.cb_comptoir) {
        updates.cb_comptoir = increment(1);
      }
      if (selectedPayments.ticket_resto) {
        updates.ticket_resto = increment(1);
      }
      
      // Vérifier si au moins un mode de paiement est sélectionné
      if (Object.keys(updates).length > 0) {
        await updateDoc(paiementRef, updates);
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour des compteurs:', error);
    }
  };

  const openPaymentModal = (clientId) => {
    setCurrentClientId(clientId);
    setShowModal(true);
  };

  const PaymentModal = () => {
    const clientData = clients.find(client => client.id === currentClientId);
    const totalAmount = clientData?.totalCommande || 0;
    
    const [localPaymentAmounts, setLocalPaymentAmounts] = useState({
      espece: '',
      cb_comptoir: '',
      ticket_resto: ''
    });
    
    const selectedCount = Object.values(selectedPayments).filter(Boolean).length;
    
    // Calcul du reste à payer
    const totalEntered = Object.entries(selectedPayments)
      .filter(([_, isSelected]) => isSelected)
      .reduce((sum, [method]) => {
        const amount = parseFloat(localPaymentAmounts[method]) || 0;
        return sum + parseFloat(amount.toFixed(2));
      }, 0);

    const remainingAmount = parseFloat((totalAmount - totalEntered).toFixed(2));

    const handleAmountChange = (key, value) => {
      setLocalPaymentAmounts(prev => ({
        ...prev,
        [key]: value
      }));
    };

    const handleValidate = async () => {
      const hasSelectedPayment = Object.values(selectedPayments).some(value => value);
      
      if (!hasSelectedPayment) {
        alert('Veuillez sélectionner au moins un mode de paiement');
        return;
      }

      // Si un seul moyen de paiement est sélectionné
      if (selectedCount === 1) {
        const selectedMethod = Object.entries(selectedPayments).find(([_, isSelected]) => isSelected)[0];
        
        try {
          const clientRef = doc(db, 'clients', currentClientId);
          await updateDoc(clientRef, {
            'paiement.status': 'payé',
            'paiement.methods': [{
              type: selectedMethod,
              amount: totalAmount
            }]
          });

          await updatePaymentCounters();
          setShowModal(false);
          setSelectedPayments({
            espece: false,
            cb_comptoir: false,
            ticket_resto: false
          });
          setPaymentAmounts({
            espece: '',
            cb_comptoir: '',
            ticket_resto: ''
          });
        } catch (error) {
          console.error('Erreur lors de la mise à jour du paiement:', error);
        }
        return;
      }

      // Pour plusieurs moyens de paiement, vérifier que la somme correspond
      const totalEntered = Object.entries(selectedPayments)
        .filter(([_, isSelected]) => isSelected)
        .reduce((sum, [method]) => {
          const amount = parseFloat(localPaymentAmounts[method]) || 0;
          return sum + parseFloat(amount.toFixed(2));
        }, 0);

      // Comparaison avec une tolérance pour les erreurs d'arrondi
      if (Math.abs(totalEntered - totalAmount) > 0.01) {
        alert('La somme des montants ne correspond pas au total de la commande');
        return;
      }

      try {
        const clientRef = doc(db, 'clients', currentClientId);
        await updateDoc(clientRef, {
          'paiement.status': 'payé',
          'paiement.methods': Object.entries(selectedPayments)
            .filter(([_, isSelected]) => isSelected)
            .map(([method]) => ({
              type: method,
              amount: parseFloat(Number(localPaymentAmounts[method]).toFixed(2))
            }))
        });

        await updatePaymentCounters();
        setShowModal(false);
        setSelectedPayments({
          espece: false,
          cb_comptoir: false,
          ticket_resto: false
        });
        setPaymentAmounts({
          espece: '',
          cb_comptoir: '',
          ticket_resto: ''
        });
      } catch (error) {
        console.error('Erreur lors de la mise à jour du paiement:', error);
      }
    };

    return (
      <div className="mypaiement-modal-overlay">
        <div className="mypaiement-modal">
          <div className="mypaiement-modal-header">
            <h2 className="mypaiement-modal-title">
              <FaCreditCard className="mypaiement-modal-icon" />
              Sélectionner le(s) mode(s) de paiement
            </h2>
            <button
              onClick={() => setShowModal(false)}
              className="mypaiement-modal-close"
            >
              <FaTimes />
            </button>
          </div>
          
          <div className="mypaiement-modal-body">
            <div className="mypaiement-amount-info">
              <div className="mypaiement-amount-total">
                <FaEuroSign className="mypaiement-amount-icon" />
                <span className="mypaiement-amount-label">Montant total :</span>
                <span className="mypaiement-amount-value">{totalAmount.toFixed(2)}€</span>
              </div>
              <div className="mypaiement-amount-remaining">
                <span className="mypaiement-remaining-label">Reste à payer :</span>
                <span className="mypaiement-remaining-value">{remainingAmount.toFixed(2)}€</span>
              </div>
            </div>
            
            <div className="mypaiement-payment-options">
              {[
                { key: 'espece', label: 'Espèces', icon: FaMoneyBillWave },
                { key: 'cb_comptoir', label: 'Carte Bleue', icon: FaCreditCard },
                { key: 'ticket_resto', label: 'Ticket Restaurant', icon: FaTicketAlt }
              ].map(({ key, label, icon: Icon }) => (
                <div key={key} className="mypaiement-payment-option">
                  <button 
                    className={`mypaiement-payment-method-btn ${selectedPayments[key] ? 'mypaiement-payment-method-selected' : ''}`}
                    onClick={() => setSelectedPayments(prev => ({
                      ...prev,
                      [key]: !prev[key]
                    }))}
                    type="button"
                  >
                    <Icon className="mypaiement-payment-method-icon" />
                    <span>{label}</span>
                    {selectedPayments[key] && <FaCheck className="mypaiement-payment-check" />}
                  </button>
                  {selectedPayments[key] && selectedCount > 1 && (
                    <div className="mypaiement-amount-input-container">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max={totalAmount}
                        value={localPaymentAmounts[key]}
                        onChange={(e) => handleAmountChange(key, e.target.value)}
                        className="mypaiement-amount-input"
                        placeholder="Montant"
                      />
                      <span className="mypaiement-amount-currency">€</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="mypaiement-modal-actions">
            <button 
              onClick={handleValidate}
              className="mypaiement-modal-btn mypaiement-modal-validate"
              type="button"
            >
              <FaCheck />
              Valider le paiement
            </button>
            <button 
              onClick={() => {
                setShowModal(false);
                setSelectedPayments({
                  espece: false,
                  cb_comptoir: false,
                  ticket_resto: false
                });
                setLocalPaymentAmounts({
                  espece: '',
                  cb_comptoir: '',
                  ticket_resto: ''
                });
              }}
              className="mypaiement-modal-btn mypaiement-modal-cancel"
              type="button"
            >
              <FaTimes />
              Annuler
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="mypaiement-container">
      {/* Header */}
      <div className="mypaiement-header">
        <div className="mypaiement-title-section">
          <FaCreditCard className="mypaiement-title-icon" />
          <h1 className="mypaiement-title">Gestion des Paiements</h1>
        </div>
        <p className="mypaiement-subtitle">
          Suivi et validation des paiements clients
        </p>
      </div>

      {/* Message de feedback */}
      {message && (
        <div className={`mypaiement-message mypaiement-message-${messageType}`}>
          <div className="mypaiement-message-icon">
            {messageType === 'success' && <FaCheckCircle />}
            {messageType === 'error' && <FaExclamationTriangle />}
            {messageType === 'info' && <FaInfoCircle />}
          </div>
          <span>{message}</span>
        </div>
      )}

      {/* Statistiques */}
      {showStats && (
        <div className="mypaiement-stats-section">
          <div className="mypaiement-stats-grid">
            <div className="mypaiement-stat-card">
              <div className="mypaiement-stat-icon">
                <FaShoppingCart />
              </div>
              <div className="mypaiement-stat-content">
                <div className="mypaiement-stat-value">{stats.totalClients}</div>
                <div className="mypaiement-stat-label">Commandes Total</div>
              </div>
            </div>
            <div className="mypaiement-stat-card">
              <div className="mypaiement-stat-icon">
                <FaCheckCircle />
              </div>
              <div className="mypaiement-stat-content">
                <div className="mypaiement-stat-value">{stats.paidClients}</div>
                <div className="mypaiement-stat-label">Payées</div>
              </div>
            </div>
            <div className="mypaiement-stat-card">
              <div className="mypaiement-stat-icon">
                <FaExclamationTriangle />
              </div>
              <div className="mypaiement-stat-content">
                <div className="mypaiement-stat-value">{stats.pendingClients}</div>
                <div className="mypaiement-stat-label">En Attente</div>
              </div>
            </div>
            <div className="mypaiement-stat-card">
              <div className="mypaiement-stat-icon">
                <FaEuroSign />
              </div>
              <div className="mypaiement-stat-content">
                <div className="mypaiement-stat-value">{stats.totalRevenue.toFixed(2)}€</div>
                <div className="mypaiement-stat-label">Chiffre d'Affaires</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Contrôles */}
      <div className="mypaiement-controls-section">
        <div className="mypaiement-filters">
          <div className="mypaiement-search-container">
            <FaSearch className="mypaiement-search-icon" />
            <input
              type="text"
              placeholder="Rechercher un client..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="mypaiement-search-input"
            />
          </div>
          
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="mypaiement-filter-select"
          >
            <option value="all">Tous les statuts</option>
            <option value="en_attente">En attente</option>
            <option value="payé">Payé</option>
          </select>
        </div>

        <div className="mypaiement-actions">
          <button
            onClick={() => setShowStats(!showStats)}
            className="mypaiement-toggle-stats-btn"
          >
            {showStats ? <FaArrowUp /> : <FaArrowDown />}
            {showStats ? 'Masquer' : 'Afficher'} Stats
          </button>
          
          <button
            onClick={() => setViewMode(viewMode === 'cards' ? 'list' : 'cards')}
            className="mypaiement-view-mode-btn"
          >
            {viewMode === 'cards' ? <FaList /> : <FaTh />}
            {viewMode === 'cards' ? 'Vue Liste' : 'Vue Cartes'}
          </button>
          
          <button
            onClick={exportToExcel}
            disabled={exporting}
            className="mypaiement-export-btn"
          >
            {exporting ? <FaSpinner className="spinning" /> : <FaFileExcel />}
            {exporting ? 'Export...' : 'Exporter'}
          </button>
        </div>
      </div>

      {/* Contenu principal */}
      {filteredClients.length > 0 ? (
        <div className={`mypaiement-content ${viewMode === 'list' ? 'mypaiement-content-list' : 'mypaiement-content-cards'}`}>
          {filteredClients.map((client) => (
            <div key={client.id} className={`mypaiement-client-card ${client.paiement.status === 'en_attente' ? 'mypaiement-card-pending' : ''}`}>
              <div className="mypaiement-card-header">
                <div className="mypaiement-client-info">
                  <div className={`mypaiement-client-avatar ${client.paiement.status === 'en_attente' ? 'mypaiement-avatar-pending' : ''}`}>
                    <FaUser />
                  </div>
                  <div className="mypaiement-client-details">
                    <h3 className="mypaiement-client-name">{client.prenom}</h3>
                    <p className="mypaiement-client-id">Commande #{client.idclient}</p>
                  </div>
                </div>
                <div className="mypaiement-status-section">
                  {getStatusIcon(client.paiement.status)}
                  <span className={`mypaiement-status-text mypaiement-status-${client.paiement.status}`}>
                    {client.paiement.status === 'payé' ? 'Payé' : 'En Attente'}
                  </span>
                </div>
              </div>

              {/* Numéro de table mis en évidence */}
              <div className={`mypaiement-table-badge ${client.paiement.status === 'en_attente' ? 'mypaiement-table-pending' : ''}`}>
                <FaTable className="mypaiement-table-badge-icon" />
                <span className="mypaiement-table-badge-text">{client.table}</span>
              </div>

              <div className="mypaiement-card-body">
                <div className="mypaiement-client-meta">
                  <div className="mypaiement-meta-item">
                    <FaEnvelope className="mypaiement-meta-icon" />
                    <span>{client.email}</span>
                  </div>
                </div>

                {client.paiement.moyen_paiement && (
                  <div className="mypaiement-payment-methods">
                    <h4>Moyens de paiement :</h4>
                    <div className="mypaiement-payment-methods-list">
                      {client.paiement.moyen_paiement.split(', ').map((moyen, index) => (
                        <div key={index} className="mypaiement-payment-method">
                          {getPaymentMethodIcon(moyen.toLowerCase().replace(' ', '_'))}
                          <span>{moyen}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mypaiement-order-details">
                  <h4 className="mypaiement-order-title">
                    <FaShoppingCart className="mypaiement-order-icon" />
                    Détails de la commande
                  </h4>
                  {client.items && client.items.length > 0 ? (
                    <div className="mypaiement-items-list">
                      {client.items.map((item, index) => (
                        <div key={index} className="mypaiement-item">
                          <div className="mypaiement-item-header">
                            <span className="mypaiement-item-name">{item.nom}</span>
                            <span className="mypaiement-item-quantity">x{item.quantity}</span>
                          </div>
                          <div className="mypaiement-item-prices">
                            <span className="mypaiement-item-unit-price">{item.prix.toFixed(2)}€</span>
                            <span className="mypaiement-item-total-price">{(item.prix * item.quantity).toFixed(2)}€</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="mypaiement-no-items">Aucun article dans la commande.</p>
                  )}
                </div>

                <div className="mypaiement-total-section">
                  <div className="mypaiement-total-amount">
                    <FaEuroSign className="mypaiement-total-icon" />
                    <span className="mypaiement-total-value">{client.totalCommande.toFixed(2)}€</span>
                  </div>
                </div>
              </div>

              <div className="mypaiement-card-actions">
                {client.paiement.status === 'en_attente' && (
                  <button
                    onClick={() => openPaymentModal(client.id)}
                    className="mypaiement-action-btn mypaiement-pay-btn"
                  >
                    <FaCheck />
                    Marquer comme payé
                  </button>
                )}
                {client.paiement.status === 'payé' && (
                  <button
                    onClick={() => handleDeleteCommande(client.id)}
                    className="mypaiement-action-btn mypaiement-delete-btn"
                  >
                    <FaTrash />
                    Supprimer
                  </button>
                )}
                <button
                  onClick={() => handleImprimerTicket(client)}
                  className="mypaiement-action-btn mypaiement-print-btn"
                >
                  <FaPrint />
                  Imprimer
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="mypaiement-no-results">
          <FaExclamationTriangle className="mypaiement-no-results-icon" />
          <p>Aucune commande trouvée pour cette recherche.</p>
        </div>
      )}

      {showModal && <PaymentModal />}
    </div>
  );
};

export default MyPaiement;
